import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import LoginForm from '../components/LoginForm';
import ProfileBuilderNew from '../components/ProfileBuilderNew';

const REASON_MESSAGES: Record<string, string> = {
  invalid_link: 'This link is invalid or has been tampered with. Please request a new one.',
  link_expired: 'This link has expired. Please request a new one.',
  link_already_used: 'This link has already been used. Please request a new one if needed.',
  server_error: 'Something went wrong on our end. Please try again or contact support.',
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showProfileBuilder, setShowProfileBuilder] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('signup') === 'true') {
      setShowProfileBuilder(true);
    }

    // Check for reason parameter (from relay errors)
    const reason = searchParams.get('reason');
    if (reason && REASON_MESSAGES[reason]) {
      setErrorMessage(REASON_MESSAGES[reason]);
      // Clear the reason from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('reason');
      setSearchParams(newParams, { replace: true });
      // Clear the message after 10 seconds
      const timer = setTimeout(() => setErrorMessage(null), 10000);
      return () => clearTimeout(timer);
    }

    // Check for success message from location state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 10 seconds
      const timer = setTimeout(() => setSuccessMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, location.state, setSearchParams]);

  const getSafeReturnUrl = () => {
    const returnUrl = searchParams.get('returnUrl');

    // If no returnUrl provided, default to /vybes
    if (!returnUrl) {
      return '/vybes';
    }

    // Sanitize to ensure it's an internal path only
    // Must start with / and not contain protocol or domain
    const trimmed = returnUrl.trim();
    if (!trimmed.startsWith('/') || trimmed.includes('://') || trimmed.includes('//')) {
      console.warn('[LoginPage] Invalid returnUrl detected, using default:', returnUrl);
      return '/vybes';
    }

    return trimmed;
  };

  const handleClose = () => {
    navigate(getSafeReturnUrl(), { replace: true });
  };

  const handleCloseAll = () => {
    setShowProfileBuilder(false);
    navigate(getSafeReturnUrl(), { replace: true });
  };

  // PROFILE BUILDER VIEW - fullscreen, no wrapper
  if (showProfileBuilder) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="relative w-full max-w-md h-[90vh] max-h-[800px]">
          <button
            type="button"
            onClick={handleCloseAll}
            className="absolute -top-2 -right-2 inline-flex items-center justify-center
                       h-10 w-10 rounded-full bg-theme-cards border border-secondary/40 text-secondary
                       hover:bg-secondary/10 focus:outline-none z-50 shadow-lg"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div className="w-full h-full rounded-2xl overflow-hidden shadow-2xl">
            <ProfileBuilderNew
              onComplete={handleCloseAll}
              onLoginClick={() => {
                setShowProfileBuilder(false);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // LOGIN FORM VIEW
  return (
    <div className="min-h-screen bg-theme-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative"
      >
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3"
          >
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-500 text-sm flex-1">{errorMessage}</p>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-500/60 hover:text-red-500"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </motion.div>
        )}

        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-start gap-3"
          >
            <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-green-500 text-sm flex-1">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-500/60 hover:text-green-500"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </motion.div>
        )}

        <div className="bg-theme-cards border border-theme-cards rounded-2xl shadow-card p-6">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 inline-flex items-center justify-center
                       h-8 w-8 rounded-full border border-secondary/40 text-secondary
                       hover:bg-secondary/10 focus:outline-none z-10"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <LoginForm
            onSignupClick={() => setShowProfileBuilder(true)}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
